import { useAuth } from "@agentech/auth-client";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { config } from "../../config";

interface UserDetail {
  userId: string;
  email: string;
  emailVerified: boolean;
  name?: string;
  status: string;
  enabled: boolean;
  createdAt: string;
  lastModified: string;
  mfaEnabled: boolean;
}

interface Membership {
  organizationId: string;
  userId: string;
  role: string;
  permissions: string[];
  joinedAt: string;
}

export function UserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user: authUser, refreshSession } = useAuth();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const isImpersonating = authUser?.isImpersonating ?? false;

  // Impersonation
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState("");

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/users/${id}`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch user");

      const data = await response.json();
      setUser(data.user);
      setMemberships(data.memberships ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleDisable = async () => {
    try {
      setActionMessage(null);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/users/${id}/disable`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage("User disabled successfully");
        fetchUser();
      } else {
        setActionMessage(data.error ?? "Failed to disable user");
      }
    } catch {
      setActionMessage("Failed to disable user");
    }
  };

  const handleEnable = async () => {
    try {
      setActionMessage(null);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/users/${id}/enable`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage("User enabled successfully");
        fetchUser();
      } else {
        setActionMessage(data.error ?? "Failed to enable user");
      }
    } catch {
      setActionMessage("Failed to enable user");
    }
  };

  const handleResetPassword = async () => {
    try {
      setActionMessage(null);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/users/${id}/reset-password`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setActionMessage(data.message);
      } else {
        setActionMessage(data.error ?? "Failed to reset password");
      }
    } catch {
      setActionMessage("Failed to reset password");
    }
  };

  const handleStartImpersonation = async () => {
    try {
      setActionMessage(null);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/impersonate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          targetUserId: id,
          reason: impersonateReason,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowImpersonateModal(false);
        setImpersonateReason("");
        setActionMessage(`Now impersonating ${data.targetEmail}`);
        // Refresh session so auth-client picks up impersonation state
        await refreshSession();
      } else {
        setActionMessage(data.error ?? "Failed to start impersonation");
      }
    } catch {
      setActionMessage("Failed to start impersonation");
    }
  };

  const handleEndImpersonation = async () => {
    if (!authUser?.impersonationSessionId) return;
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/admin/impersonate?sessionId=${authUser.impersonationSessionId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (response.ok) {
        setActionMessage("Impersonation ended");
        await refreshSession();
      }
    } catch {
      setActionMessage("Failed to end impersonation");
    }
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Loading user...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="admin-page">
        <div className="admin-error">
          <p>{error ?? "User not found"}</p>
          <Link to="/admin/users">Back to Users</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <Link to="/admin/users" className="btn-link">
            &larr; Users
          </Link>
          <h1>{user.email}</h1>
          <p className="text-muted">{user.userId}</p>
        </div>
      </header>

      {actionMessage && <div className="form-info">{actionMessage}</div>}

      <div className="admin-card">
        <h2>User Details</h2>
        <dl className="detail-list">
          <dt>User ID</dt>
          <dd>{user.userId}</dd>
          <dt>Email</dt>
          <dd>
            {user.email}{" "}
            {user.emailVerified ? (
              <span className="badge badge-success">Verified</span>
            ) : (
              <span className="badge badge-warning">Unverified</span>
            )}
          </dd>
          <dt>Name</dt>
          <dd>{user.name ?? "-"}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`badge ${user.status === "CONFIRMED" ? "badge-success" : ""}`}>
              {user.status}
            </span>
          </dd>
          <dt>Enabled</dt>
          <dd>
            {user.enabled ? (
              <span className="badge badge-success">Yes</span>
            ) : (
              <span className="badge badge-danger">No</span>
            )}
          </dd>
          <dt>MFA</dt>
          <dd>{user.mfaEnabled ? "Enabled" : "Disabled"}</dd>
          <dt>Created</dt>
          <dd>{new Date(user.createdAt).toLocaleString()}</dd>
          <dt>Last Modified</dt>
          <dd>{new Date(user.lastModified).toLocaleString()}</dd>
        </dl>
      </div>

      <div className="admin-card">
        <h2>Actions</h2>
        <div className="action-buttons">
          {user.enabled ? (
            <button type="button" className="btn btn-danger" onClick={handleDisable}>
              Disable Account
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={handleEnable}>
              Enable Account
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={handleResetPassword}>
            Reset Password
          </button>
          {!isImpersonating ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowImpersonateModal(true)}
            >
              Impersonate User
            </button>
          ) : (
            <button type="button" className="btn btn-danger" onClick={handleEndImpersonation}>
              End Impersonation
            </button>
          )}
        </div>
      </div>

      {showImpersonateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Impersonate User</h2>
            <p>
              You are about to impersonate <strong>{user.email}</strong>. This action will be logged
              in the audit trail.
            </p>
            <div className="form-group">
              <label htmlFor="reason">Reason (required, min 10 characters)</label>
              <textarea
                id="reason"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="Describe why you need to impersonate this user..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowImpersonateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartImpersonation}
                disabled={impersonateReason.trim().length < 10}
              >
                Start Impersonation
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <h2>Organization Memberships</h2>
        {memberships.length === 0 ? (
          <p className="text-muted">No organization memberships.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.organizationId}>
                  <td>
                    <Link to={`/admin/organizations/${m.organizationId}`}>{m.organizationId}</Link>
                  </td>
                  <td>
                    <span className="badge">{m.role}</span>
                  </td>
                  <td>{new Date(m.joinedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
